import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
  TextInput,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import backIcon from '../../assets/icons/back.png';
import logo from '../../assets/logo.png';
import { API_URL } from '../../config';

import { createLogger } from '../../utils/logger';
const logger = createLogger('KeyHolderDetails');

interface KeyHolderDetailsScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<any, any>;
}

interface RelationshipData {
  id: string;
  primaryUserId: string;
  secondaryUserId: string;
  role: 'successor' | 'keyholder';
  status: string;
  validationQuestion?: string;
  phone?: string;
  emailNotifications: boolean;
  smsNotifications: boolean;
  twoFaRequired: boolean;
  createdAt: string;
  updatedAt: string;
  secondaryUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
  };
}

interface InvitationData {
  id: string;
  inviteeEmail: string;
  role: 'successor' | 'keyholder';
  status: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  validationQuestion?: string;
  createdAt: string;
  expiresAt: string;
}

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value}</Text>
    <View style={styles.separator} />
  </View>
);

const KeyHolderDetailsScreen: React.FC<KeyHolderDetailsScreenProps> = ({ navigation, route }) => {
  const { keyholder, relationshipId, isPending, invitationId } = route.params;
  const [isResending, setIsResending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [relationshipData, setRelationshipData] = useState<RelationshipData | null>(null);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);

  // Inline-edit state. Only the fields that the backend PUT /api/relationships/:id
  // actually persists are editable: phone, validationQuestion, emailNotifications,
  // smsNotifications. Names + email belong to the keyholder's own account and are
  // not updatable by the primary user, so they stay read-only.
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editPhone, setEditPhone] = useState('');
  const [editValidationQuestion, setEditValidationQuestion] = useState('');
  const [editEmailNotifications, setEditEmailNotifications] = useState(false);
  const [editSmsNotifications, setEditSmsNotifications] = useState(false);

  useEffect(() => {
    loadData();
  }, [relationshipId, invitationId, isPending]);

  const loadData = async () => {
    try {
      logger.debug('Loading keyholder details:', { isPending, invitationId, relationshipId, hasKeyholderParam: !!keyholder });

      // If we have the keyholder data passed from LinkedAccountsScreen, use it
      if (isPending && keyholder) {
        logger.debug('Using invitation data from params:', keyholder);
        setInvitationData(keyholder as any);
        setLoading(false);
        return;
      }

      // If we need to fetch relationship data for active keyholder
      if (relationshipId && !isPending) {
        const token = await getAuthToken();
        if (!token) {
          Alert.alert('Error', 'Please log in again');
          setLoading(false);
          return;
        }

        logger.debug('Fetching relationship:', relationshipId);
        const response = await fetch(`${API_URL}/api/relationships/${relationshipId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          logger.debug('Relationship data loaded:', data);
          // The API returns { success: true, relationship: {...} }
          setRelationshipData(data.relationship || data);
        } else {
          const errorText = await response.text();
          logger.error('Failed to fetch relationship:', response.status, errorText);
          Alert.alert('Error', `Failed to load keyholder details: ${response.status}`);
        }
      } else {
        logger.error('Missing required data:', { relationshipId, invitationId, isPending });
        Alert.alert('Error', 'Missing keyholder information');
      }
    } catch (error) {
      logger.error('Error loading data:', error);
      Alert.alert('Error', 'Failed to load keyholder details: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async () => {
    setIsResending(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in again');
        return;
      }

      // Get the invitation ID from either the route params or the invitation data
      const actualInvitationId = invitationId || invitationData?.id;

      if (!actualInvitationId) {
        logger.error('No invitation ID available:', { invitationId, invitationDataId: invitationData?.id });
        Alert.alert('Error', 'Invitation ID not found');
        return;
      }

      logger.debug('Resending invitation:', actualInvitationId);
      const response = await fetch(`${API_URL}/api/invitations/${actualInvitationId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        Alert.alert('Success', 'Invitation resent successfully');
      } else {
        const errorData = await response.text();
        logger.error('Resend failed:', response.status, errorData);
        Alert.alert('Error', `Failed to resend invitation: ${response.status}`);
      }
    } catch (error) {
      logger.error('Error resending invitation:', error);
      Alert.alert('Error', 'Failed to resend invitation: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelInvitation = () => {
    // Get the invitation ID from either the route params or the invitation data
    const actualInvitationId = invitationId || invitationData?.id;

    if (!actualInvitationId) {
      Alert.alert('Error', 'Invitation ID not found');
      return;
    }

    Alert.alert(
      'Cancel Invitation',
      'Are you sure you want to cancel this invitation?',
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Yes, Cancel',
          style: 'destructive',
          onPress: async () => {
            try {
              logger.debug('Cancelling invitation with ID:', actualInvitationId);
              const token = await getAuthToken();
              if (!token) {
                Alert.alert('Error', 'Please log in again');
                return;
              }

              const response = await fetch(`${API_URL}/api/invitations/${actualInvitationId}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });

              logger.debug('Cancel response status:', response.status);

              if (response.ok) {
                Alert.alert('Success', 'Invitation cancelled successfully', [
                  { text: 'OK', onPress: () => navigation.navigate('LinkedAccounts') }
                ]);
              } else {
                const errorText = await response.text();
                logger.error('Cancel failed:', response.status, errorText);
                Alert.alert('Error', `Failed to cancel invitation: ${response.status}`);
              }
            } catch (error) {
              logger.error('Error cancelling invitation:', error);
              Alert.alert('Error', 'Failed to cancel invitation: ' + (error instanceof Error ? error.message : 'Unknown error'));
            }
          }
        }
      ]
    );
  };

  const seedEditState = (rel: RelationshipData) => {
    setEditPhone(rel.phone ?? rel.secondaryUser?.phoneNumber ?? '');
    setEditValidationQuestion(rel.validationQuestion ?? '');
    setEditEmailNotifications(Boolean(rel.emailNotifications));
    setEditSmsNotifications(Boolean(rel.smsNotifications));
  };

  const handleEdit = () => {
    if (!relationshipData) return;
    seedEditState(relationshipData);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (relationshipData) {
      seedEditState(relationshipData);
    }
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!relationshipData) return;

    if (!editValidationQuestion.trim()) {
      Alert.alert('Validation Question Required', 'Please enter a validation question.');
      return;
    }

    const relId = relationshipId || relationshipData.id;
    if (!relId) {
      Alert.alert('Error', 'Missing relationship id');
      return;
    }

    // Build a delta so we only send fields the user actually changed.
    const payload: Record<string, string | boolean> = {};
    const trimmedPhone = editPhone.trim();
    const trimmedQuestion = editValidationQuestion.trim();
    if (trimmedPhone !== (relationshipData.phone ?? '')) {
      payload.phone = trimmedPhone;
    }
    if (trimmedQuestion !== (relationshipData.validationQuestion ?? '')) {
      payload.validationQuestion = trimmedQuestion;
    }
    if (editEmailNotifications !== Boolean(relationshipData.emailNotifications)) {
      payload.emailNotifications = editEmailNotifications;
    }
    if (editSmsNotifications !== Boolean(relationshipData.smsNotifications)) {
      payload.smsNotifications = editSmsNotifications;
    }

    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in again');
        return;
      }

      const response = await fetch(`${API_URL}/api/relationships/${relId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const updated = data.relationship || data;
        setRelationshipData((prev) => (prev ? { ...prev, ...updated } : updated));
        setIsEditing(false);
      } else {
        const errorText = await response.text();
        logger.error('Update relationship failed:', response.status, errorText);
        Alert.alert('Error', `Failed to save changes: ${response.status}`);
      }
    } catch (error) {
      logger.error('Error updating relationship:', error);
      Alert.alert('Error', 'Failed to save changes: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    navigation.navigate('DeleteKeyHolder', {
      relationshipId: relationshipId || relationshipData?.id,
      keyholder: relationshipData?.secondaryUser ? {
        name: `${relationshipData.secondaryUser.firstName} ${relationshipData.secondaryUser.lastName}`,
        email: relationshipData.secondaryUser.email
      } : null,
    });
  };

  const handleCreateNewInvitation = () => {
    // Navigate to AddKeyHolderScreen with pre-populated data
    navigation.navigate('AddKeyHolder', {
      prefilledData: {
        email: invitationData?.inviteeEmail,
        firstName: invitationData?.firstName,
        lastName: invitationData?.lastName,
        phone: invitationData?.phone,
        validationQuestion: invitationData?.validationQuestion,
      }
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={[styles.title, { marginTop: 20, color: '#FFFFFF' }]}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Get data to display
  const displayData = isPending ? invitationData : relationshipData;
  const email = isPending
    ? (invitationData?.inviteeEmail || '')
    : (relationshipData?.secondaryUser?.email || '');
  const firstName = isPending
    ? (invitationData?.firstName || 'Not provided')
    : (relationshipData?.secondaryUser?.firstName || 'Not provided');
  const lastName = isPending
    ? (invitationData?.lastName || 'Not provided')
    : (relationshipData?.secondaryUser?.lastName || 'Not provided');
  const phone = isPending
    ? (invitationData?.phone || 'Not provided')
    : (relationshipData?.phone || relationshipData?.secondaryUser?.phoneNumber || 'Not provided');
  const validationQuestion = isPending
    ? (invitationData?.validationQuestion || 'Not provided')
    : (relationshipData?.validationQuestion || 'Not provided');
  const emailNotify = relationshipData?.emailNotifications || false;
  const textNotify = relationshipData?.smsNotifications || false;
  const expiresAt = invitationData?.expiresAt;

  // Check if invitation is pending or revoked/canceled
  const isInvitationPending = invitationData?.status?.toLowerCase() === 'pending';
  const isInvitationRevoked = invitationData?.status?.toLowerCase() === 'revoked' || invitationData?.status?.toLowerCase() === 'canceled';

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.navigate('LinkedAccounts')}
        >
          <Image
            source={backIcon}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={logo}
          style={styles.logo}
          resizeMode="contain"
        />
        <View style={{ width: 32 }} />
      </View>

      <Text style={styles.title}>Key Holder Details</Text>

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Invitation Status Banner - at top */}
          {isPending && (
            <>
              {isInvitationPending && (
                <>
                  <View style={styles.statusBanner}>
                    <Text style={styles.statusBannerText}>⏳ Invitation Pending</Text>
                    <Text style={styles.statusDescription}>
                      Waiting for {email} to accept the invitation
                    </Text>
                    {expiresAt && (
                      <Text style={styles.expiryText}>
                        Expires: {new Date(expiresAt).toLocaleDateString()}
                      </Text>
                    )}
                  </View>

                  {/* Resend and Cancel buttons */}
                  <View style={styles.pendingButtonContainer}>
                    <TouchableOpacity
                      style={[styles.button, styles.resendButton, isResending && { opacity: 0.6 }]}
                      onPress={handleResendInvitation}
                      disabled={isResending}
                    >
                      {isResending ? (
                        <ActivityIndicator color="#29BFC0" size="small" />
                      ) : (
                        <Text style={styles.resendButtonText}>Resend</Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.button, styles.cancelButton]}
                      onPress={handleCancelInvitation}
                    >
                      <Text style={styles.buttonText}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}

              {isInvitationRevoked && (
                <>
                  <View style={[styles.statusBanner, styles.revokedBanner]}>
                    <Text style={[styles.statusBannerText, styles.revokedText]}>❌ Invitation Canceled</Text>
                    <Text style={[styles.statusDescription, styles.revokedText]}>
                      This invitation was canceled and is no longer valid
                    </Text>
                  </View>

                  {/* Create New Invitation button */}
                  <TouchableOpacity
                    style={[styles.button, styles.createNewButton]}
                    onPress={handleCreateNewInvitation}
                  >
                    <Text style={styles.buttonText}>Create New Invitation</Text>
                  </TouchableOpacity>
                </>
              )}
            </>
          )}

          {/* Key Holder Details — names + email always read-only (backend
              doesn't expose them on PUT /api/relationships/:id; they live
              on the keyholder's own user account). */}
          <InfoItem label="Email" value={email} />
          <InfoItem label="First Name" value={firstName} />
          <InfoItem label="Last Name" value={lastName} />

          {isEditing && !isPending && relationshipData ? (
            <>
              <View style={styles.infoItem}>
                <Text style={styles.label}>Phone Number</Text>
                <TextInput
                  style={styles.editInput}
                  value={editPhone}
                  onChangeText={setEditPhone}
                  placeholder="Enter phone number"
                  placeholderTextColor="#999"
                  keyboardType="phone-pad"
                  editable={!isSaving}
                />
              </View>
              <View style={styles.infoItem}>
                <Text style={styles.label}>Validation Question</Text>
                <TextInput
                  style={[styles.editInput, styles.editInputMultiline]}
                  value={editValidationQuestion}
                  onChangeText={setEditValidationQuestion}
                  placeholder="Enter validation question"
                  placeholderTextColor="#999"
                  multiline
                  numberOfLines={3}
                  editable={!isSaving}
                />
              </View>
            </>
          ) : (
            <>
              <InfoItem label="Phone Number" value={phone} />
              <InfoItem label="Validation Question" value={validationQuestion} />
            </>
          )}

          {/* Notification settings - only for active keyholders */}
          {!isPending && relationshipData && (
            <View style={styles.notificationSection}>
              <Text style={styles.sectionTitle}>How Your Key Holder is Notified</Text>
              <View style={styles.notificationRow}>
                <Text style={styles.notificationLabel}>Email</Text>
                {isEditing ? (
                  <Switch
                    value={editEmailNotifications}
                    onValueChange={setEditEmailNotifications}
                    trackColor={{ false: '#E5E5EA', true: '#29BFC0' }}
                    thumbColor="#FFFFFF"
                    disabled={isSaving}
                  />
                ) : (
                  <Text style={styles.notificationValue}>{emailNotify ? '✅ Enabled' : '❌ Disabled'}</Text>
                )}
              </View>
              <View style={styles.separator} />
              <View style={styles.notificationRow}>
                <Text style={styles.notificationLabel}>Text Message</Text>
                {isEditing ? (
                  <Switch
                    value={editSmsNotifications}
                    onValueChange={setEditSmsNotifications}
                    trackColor={{ false: '#E5E5EA', true: '#29BFC0' }}
                    thumbColor="#FFFFFF"
                    disabled={isSaving}
                  />
                ) : (
                  <Text style={styles.notificationValue}>{textNotify ? '✅ Enabled' : '❌ Disabled'}</Text>
                )}
              </View>
              <View style={styles.separator} />
            </View>
          )}

          {/* Bottom action buttons — only for active keyholders. Layout
              switches between read mode (Back / Edit / Delete) and edit
              mode (Cancel / Save Changes). */}
          {!isPending && relationshipData && !isEditing && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.backToListButton]}
                onPress={() => navigation.navigate('LinkedAccounts')}
              >
                <Text style={styles.buttonText}>Back</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.editButton]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Edit</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.deleteButton]}
                onPress={handleDelete}
              >
                <Text style={styles.buttonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          )}
          {!isPending && relationshipData && isEditing && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelEditButton, isSaving && { opacity: 0.6 }]}
                onPress={handleCancelEdit}
                disabled={isSaving}
              >
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.saveButton, isSaving && { opacity: 0.6 }]}
                onPress={handleSaveEdit}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <Text style={styles.buttonText}>Save Changes</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  statusBanner: {
    backgroundColor: '#FFF3CD',
    borderLeftWidth: ms(4),
    borderLeftColor: '#FFA500',
    borderRadius: ms(8),
    padding: ms(15),
    marginBottom: ms(20),
  },
  statusBannerText: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#856404',
    marginBottom: ms(5),
  },
  statusDescription: {
    fontSize: ms(14, 0.3),
    color: '#856404',
    marginTop: ms(5),
  },
  expiryText: {
    fontSize: ms(12, 0.3),
    color: '#856404',
    marginTop: ms(8),
    fontStyle: 'italic',
  },
  notificationSection: {
    marginTop: ms(10),
    marginBottom: ms(20),
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  notificationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  notificationLabel: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  notificationValue: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
  },
  pendingButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(30),
    gap: ms(10),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(40),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  resendButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: ms(2),
    borderColor: '#29BFC0',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  deleteButton: {
    backgroundColor: '#ED8585',
  },
  cancelButton: {
    backgroundColor: '#ED8585',
  },
  cancelEditButton: {
    backgroundColor: '#9CA3AF',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
  },
  editInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#29BFC0',
    borderRadius: ms(10),
    paddingVertical: ms(10),
    paddingHorizontal: ms(12),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  editInputMultiline: {
    minHeight: s(70),
    textAlignVertical: 'top',
  },
  createNewButton: {
    backgroundColor: '#29BFC0',
    marginBottom: ms(30),
  },
  revokedBanner: {
    backgroundColor: '#FFE5E5',
    borderLeftColor: '#ED8585',
  },
  revokedText: {
    color: '#C03030',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  resendButtonText: {
    color: '#29BFC0',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
});

export default KeyHolderDetailsScreen;
